/**
 * TranscriptTab — NMW-1166 (parent NMW-1161), asset-detail "Transcripción" tab.
 *
 * Replaces the hard-coded "Sin transcripción todavía" EmptyState that stood at
 * `_bundle.tsx:2110`. That placeholder was a fabricated empty state: it said
 * "no transcript" for every asset, including fully enriched ones, because the
 * data was never read. This component reads it.
 *
 * Per-surface decomposition (CLAUDE.md §12 rejection 7): this file is the tab,
 * not a wholesale decomposition of `_bundle.tsx`. The bundle keeps one line.
 *
 * SOURCE OF TRUTH
 *   `api.queries.captionData.getTimedTranscript` (NMW-1165) →
 *     { segments: Array<{transcript?, caption?, startTimeMs, endTimeMs, sceneIndex}>,
 *       truncated: boolean, limit: number }
 *   sorted ascending by startTimeMs. The object wrapper is deliberate upstream:
 *   truncation can never be silent, and `segments === []` is a real answer
 *   rather than an ambiguous bare array.
 *
 *   `api.analysis.queries.getEnrichmentStatus` (analysis/queries.ts:214) tells
 *   an empty transcript apart from an un-analysed asset. Without it the two
 *   collapse into the same "[]" and the screen would lie about one of them.
 *
 * THE FOUR HONEST STATES (constitution: honest-state contract)
 *   undefined                        → skeleton              (still loading)
 *   never analysed                   → CTA to run the analysis
 *   analysed, enrichment produced 0  → "no se detectó voz"    (a real answer)
 *   query threw                      → error toast + honest error panel,
 *                                      NEVER a fabricated empty transcript
 *
 *   Unknown/other pipeline states (`processing`, `failed`, or a status this UI
 *   does not recognise) get their own branches and fall CLOSED: an unrecognised
 *   status renders "estado desconocido", never "no speech detected".
 *
 * SEEKING — deliberately absent, and why.
 *   The issue asks for click-to-seek "where a player exists". A player IS
 *   mounted above the tabs on this screen (`AssetPlayer` from
 *   `screens/pending/_player`), but it exposes no seek handle: `seekTo` is a
 *   closure private to that component — no ref, no imperative handle, no prop.
 *   The one seek path that exists in this codebase is MarkerEditorPage's, which
 *   captures the underlying `<video>` node through its own `PlayerSurface`
 *   wrapper around a DIFFERENT player (`components/asset-player`). Reaching
 *   into this screen's player would mean either editing that shared player or
 *   duplicating the DOM-capture trick here — i.e. adding the second seek path
 *   the issue explicitly forbids. So the lines are time-coded and inert. When a
 *   seek handle is exported from the detail-screen player, wire `onSeekMs` (the
 *   optional prop below) and the rows become buttons; nothing else changes.
 *
 * SCALE — paginated, not virtualised.
 *   The upstream query is bounded at 8,000 segments, so the worst case is real.
 *   Rows render `PAGE_SIZE` at a time behind a "Mostrar más" control: same DOM
 *   ceiling as a windowing library, no scroll-position machinery, and it keeps
 *   the whole rendered range in the DOM for Ctrl-F and for screen readers.
 */
/*
 * MEDIA ANALYSIS v2 (CONTRACTS.md section 16.2 "Transcript tab", section 9.10, D-16; card MA-82)
 *
 *   An asset with a v2 run reads its cue-level transcript from
 *   `mediaAnalysis.queries.listTranscriptCues({ runId })` (through Convex's
 *   `usePaginatedQuery`, 500 cues a page) under the banner "Transcripción
 *   automática"; a cue whose confidence is below 0.5 is shaded; a click seeks
 *   to the cue's `startMs` through `onSeekMs` (the player applies the origin
 *   correction of time rule 9); the WebVTT and SubRip downloads are built in
 *   the browser from the cues — exactly the cues of the files in the private
 *   bucket — so no read of that bucket is ever signed for a browser. A run
 *   still running is shown "en proceso": its cues exist once `persistTranscript`
 *   ran after the understand stage (D-16), before the caption stage ends.
 *
 *   The run comes from `mediaAnalysis.queries.getAnalysisStatus`: the current
 *   (completed) run, else the latest run while it is running. A v1 asset (no
 *   run) keeps the per-scene view above, unchanged. `getEnrichmentStatus` names
 *   the current run too (`currentRunId`, card MA-82), so a v2 asset whose run
 *   status has not arrived yet is a skeleton, never the v1 view for an instant.
 */
import React, { Component, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery, usePaginatedQuery } from 'convex/react';
import { api } from '@convex/api';
import {
  Btn,
  EmptyState,
  Panel,
  Pill,
  SkeletonList,
  useToast,
} from '../../components/primitives';
import { fmtClock } from '../../lib/format';
import { navigate } from '../../lib/nav';

// The generated `api` type carries the media-analysis modules only after a
// person runs codegen (rule 10); the runtime object resolves any path. The
// precedent is `foldersApi` in content/_bundle.tsx.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mediaAnalysisApi: any = (api as any).mediaAnalysis;

/** Rows added per "Mostrar más" press. */
const PAGE_SIZE = 200;

/**
 * Same id guard the rest of the bundle uses: a malformed id would reach a
 * `v.id(...)` validator and throw an ArgumentValidationError past the error
 * boundary instead of rendering a state.
 */
const isConvexId = (s?: string): boolean => !!s && /^[a-z0-9]{20,}$/.test(s);

type TranscriptSegment = {
  transcript?: string;
  caption?: string;
  startTimeMs: number;
  endTimeMs: number;
  sceneIndex: number;
};

type TimedTranscript = {
  segments: TranscriptSegment[];
  truncated: boolean;
  limit: number;
};

type EnrichmentStatus = {
  /** Card MA-82: the asset's current v2 run, when it has one. */
  currentRunId?: string | null;
  analysisStatus?: string;
  /**
   * True only when a stage-2 ENRICHMENT job completed. Stage-1 jobs carry no
   * `mode`, so `analysisStatus === 'completed'` alone does not mean the
   * transcription pipeline ever ran. See STATE 3 below.
   */
  enrichmentCompleted?: boolean;
  enrichmentJobId?: string | null;
  enrichmentResultCount?: number;
};

/* ------------------------------------------------------------------ *
 * The v2 run and its cues (card MA-82)
 * ------------------------------------------------------------------ */

type RunStage = 'extract' | 'understand' | 'caption' | 'persist';
type ModalityWord = 'pending' | 'completed' | 'skipped' | 'failed';

/** `RunStatusView` of convex/mediaAnalysis/queries.ts (section 16.1). */
export type RunStatusView = {
  runId: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled' | string;
  stage: RunStage | string;
  stageState: 'queued' | 'submitted' | 'failed' | string;
  modalities: Record<'frames' | 'transcript' | 'captions' | 'index', ModalityWord | string>;
  failureReason: string | null;
  completedAt: number | null;
  stale: boolean;
};

type AnalysisStatus = { current: RunStatusView | null; latest: RunStatusView | null };

/** `CueView` of convex/mediaAnalysis/queries.ts: integer milliseconds from the run's P0. */
export type TranscriptCue = {
  _id: string;
  cueIndex: number;
  startMs: number;
  endMs: number;
  text: string;
  language: string;
  confidence: number;
  speaker: string | null;
};

/** Failure reasons in the console's Spanish (section 16.2 "Words on screen"). */
export const FAILURE_WORDS: Record<string, string> = {
  frames_failed: 'Fallo en fotogramas',
  transcript_failed: 'Fallo en transcripción',
  captions_failed: 'Fallo en subtítulos',
  index_failed: 'Fallo en el índice',
  rendition_changed: 'Archivo reemplazado',
  asset_removed: 'Recurso eliminado',
  operator_cancelled: 'Cancelado por un operador',
  run_timeout: 'Tiempo agotado',
};

/** A reason this build does not know is shown as its code, never as a reassuring word. */
export function failureWord(reason: string | null | undefined): string {
  if (!reason) return 'Motivo no registrado';
  return FAILURE_WORDS[reason] ?? `Motivo desconocido (${reason})`;
}

/** A cue below this confidence is shaded (section 16.2). */
export const LOW_CONFIDENCE = 0.5;

/** The cues' page size: `CUES_PAGE_MAX` of the query. */
const CUES_PAGE = 500;

function pad(n: number, width: number): string {
  return String(n).padStart(width, '0');
}

/** `HH:MM:SS<sep>mmm` from integer milliseconds. */
function cueTimestamp(ms: number, sep: '.' | ','): string {
  const whole = Math.max(0, Math.floor(ms));
  const h = Math.floor(whole / 3_600_000);
  const m = Math.floor((whole % 3_600_000) / 60_000);
  const s = Math.floor((whole % 60_000) / 1000);
  const frac = whole % 1000;
  return `${pad(h, 2)}:${pad(m, 2)}:${pad(s, 2)}${sep}${pad(frac, 3)}`;
}

/** The WebVTT text of the cues, in order: the same cues as the private bucket's file. */
export function cuesToWebVtt(cues: ReadonlyArray<Pick<TranscriptCue, 'startMs' | 'endMs' | 'text'>>): string {
  const blocks = cues.map((c, i) => `${i + 1}\n${cueTimestamp(c.startMs, '.')} --> ${cueTimestamp(c.endMs, '.')}\n${c.text}\n`);
  return 'WEBVTT\n' + (blocks.length > 0 ? '\n' + blocks.join('\n') : '');
}

/** The SubRip text of the cues, in order. */
export function cuesToSubRip(cues: ReadonlyArray<Pick<TranscriptCue, 'startMs' | 'endMs' | 'text'>>): string {
  return cues.map((c, i) => `${i + 1}\n${cueTimestamp(c.startMs, ',')} --> ${cueTimestamp(c.endMs, ',')}\n${c.text}\n`).join('\n');
}

/** Hands the browser a text file built here; nothing is fetched. */
function downloadTextFile(filename: string, mime: string, text: string): void {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export type TranscriptTabProps = {
  assetId: string;
  /**
   * Optional seek hook. Absent on the asset-detail screen today — see the
   * "SEEKING" note in the file header. When provided, each line becomes a
   * button that seeks to its start time.
   */
  onSeekMs?: (ms: number) => void;
};

/* ------------------------------------------------------------------ *
 * Error state
 * ------------------------------------------------------------------ */

/**
 * Rendered by the boundary below. A function component so it can raise the
 * toast the honest-state contract requires; the panel stays on screen after the
 * toast auto-dismisses so the failure does not become invisible.
 */
function TranscriptError(): React.ReactElement {
  const { toast } = useToast();
  useEffect(() => {
    // `variant: 'error'` is the ToastProvider's own key (index.tsx:733) and is
    // the one variant that does NOT auto-dismiss — a failed read stays visible.
    toast({
      variant: 'error',
      message: 'No se pudo cargar la transcripción.',
      detail: 'La consulta al backend falló.',
    });
  }, [toast]);

  return (
    <Panel title="Transcripción">
      <EmptyState
        icon="alertTriangle"
        title="No se pudo cargar la transcripción"
        body="La consulta falló. Esto NO significa que el video no tenga transcripción — vuelve a intentarlo o revisa el estado del backend."
        action={
          <Btn variant="solid" icon="refresh" onClick={() => window.location.reload()}>
            Reintentar
          </Btn>
        }
      />
    </Panel>
  );
}

/**
 * `useQuery` re-throws a Convex error during render. The nearest boundary is
 * the root `AppErrorBoundary`, which blanks the whole console — one tab's read
 * failing must cost that tab and nothing else. Caught here and degraded to the
 * honest error panel above; it never degrades to an empty transcript.
 * (Same idiom as `screens/schedules/ScheduleHealthBadge.tsx:149`.)
 */
class TranscriptBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown): void {
    // Keys, not values (V3I-INV-12): the asset id is already on screen.
    console.error('[TranscriptTab] transcript query failed', error);
  }

  override render(): ReactNode {
    if (this.state.failed) return <TranscriptError />;
    return this.props.children;
  }
}

/* ------------------------------------------------------------------ *
 * Tab
 * ------------------------------------------------------------------ */

export function TranscriptTab(props: TranscriptTabProps): React.ReactElement {
  // `key` resets the boundary when the screen is reused for another asset, so a
  // failure never sticks to an unrelated video.
  return (
    <TranscriptBoundary key={props.assetId}>
      <TranscriptTabInner {...props} />
    </TranscriptBoundary>
  );
}

function TranscriptTabInner({ assetId, onSeekMs }: TranscriptTabProps): React.ReactElement {
  const [shown, setShown] = useState(PAGE_SIZE);
  const valid = isConvexId(assetId);

  const data = useQuery(
    api.queries.captionData.getTimedTranscript,
    valid ? ({ assetId } as never) : 'skip',
  ) as TimedTranscript | undefined;

  const enrich = useQuery(
    api.analysis.queries.getEnrichmentStatus,
    valid ? ({ assetId } as never) : 'skip',
  ) as EnrichmentStatus | undefined | null;

  // MA-82 — the asset's v2 runs: the current one and the latest of any status.
  const runStatus = useQuery(
    mediaAnalysisApi.queries.getAnalysisStatus,
    valid ? ({ assetId } as never) : 'skip',
  ) as AnalysisStatus | undefined;

  // A malformed id never reaches the backend, so neither query resolves. Say
  // that plainly rather than skeleton-ing forever.
  if (!valid) {
    return (
      <Panel title="Transcripción">
        <EmptyState
          icon="alertTriangle"
          title="Identificador de video inválido"
          body="No se puede consultar la transcripción para este identificador."
        />
      </Panel>
    );
  }

  // STATE 1 — loading. A v2 asset (its current run named by getEnrichmentStatus)
  // also waits for the run's status: the v1 view must never stand in for it.
  if (data === undefined || enrich === undefined || (enrich?.currentRunId && runStatus === undefined)) {
    return (
      <Panel title="Transcripción">
        <SkeletonList rows={8} />
      </Panel>
    );
  }

  // MA-82 — a v2 run: the current (completed) run, else the latest while it runs.
  const run = runStatus?.current ?? (runStatus?.latest?.status === 'running' ? runStatus.latest : null);
  if (run) {
    return <TranscriptCues assetId={assetId} run={run} onSeekMs={onSeekMs} />;
  }
  const latestTerminal =
    runStatus?.latest && (runStatus.latest.status === 'failed' || runStatus.latest.status === 'cancelled')
      ? runStatus.latest
      : null;

  // Only segments that actually carry SPEECH are transcript lines. A row that
  // exists solely because stage-2 captioned the frame is not a transcript line
  // and must not render as one (see TranscriptLine). Since the 2026-08-20
  // rescope every row is caption-only, so this normally empties the list and
  // the honest "transcription has not run" state below takes over — which is
  // the truthful answer, not a bug.
  const segments = data.segments.filter(
    (s) => typeof s.transcript === 'string' && s.transcript.trim().length > 0,
  );

  // Non-empty wins over every status branch: real lines exist, show them.
  if (segments.length > 0) {
    const visible = segments.slice(0, shown);
    const remaining = segments.length - visible.length;

    return (
      <Panel
        title="Transcripción"
        right={
          <Pill tone="neutral" size="sm">
            {segments.length} {segments.length === 1 ? 'línea' : 'líneas'}
          </Pill>
        }
      >
        {data.truncated && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--body-muted)',
              background: 'var(--lightest-gray)',
              borderRadius: 8,
              padding: '8px 10px',
              marginBottom: 14,
            }}
          >
            Resultado truncado en {data.limit} segmentos. Se muestra una parte de
            la transcripción, no el video completo.
          </div>
        )}
        <ol
          data-testid="transcript-lines"
          style={{ listStyle: 'none', margin: 0, padding: 0 }}
        >
          {visible.map((s) => (
            <TranscriptLine
              key={`${s.sceneIndex}:${s.startTimeMs}`}
              segment={s}
              onSeekMs={onSeekMs}
            />
          ))}
        </ol>
        {remaining > 0 && (
          <div style={{ marginTop: 14, textAlign: 'center' }}>
            <Btn variant="ghost" onClick={() => setShown((n) => n + PAGE_SIZE)}>
              Mostrar más ({remaining} restantes)
            </Btn>
          </div>
        )}
      </Panel>
    );
  }

  // MA-82 — the latest v2 run failed or was cancelled and no completed run and
  // no v1 line stands: the reason, in words, never "never analysed".
  if (latestTerminal) {
    return (
      <Panel title="Transcripción">
        <EmptyState
          icon="alertTriangle"
          title={latestTerminal.status === 'cancelled' ? 'El análisis fue cancelado' : 'El análisis falló'}
          body={`No hay transcripción: ${failureWord(latestTerminal.failureReason)}.`}
          action={
            <Btn
              variant="solid"
              icon="refresh"
              onClick={() => navigate('/library/' + assetId + '/analyse')}
            >
              Reintentar análisis
            </Btn>
          }
        />
      </Panel>
    );
  }

  // Empty. Which KIND of empty? `getEnrichmentStatus` decides — never this file.
  const status = enrich?.analysisStatus ?? 'none';
  // Absent (older backend / still loading) is treated as "enrichment did NOT
  // run" — fail closed, so an unknown never becomes a "no speech" claim.
  const enrichmentCompleted = enrich?.enrichmentCompleted === true;
  const busy = !!enrich?.enrichmentJobId;

  // STATE 2 — never analysed.
  if (status === 'none') {
    return (
      <Panel title="Transcripción">
        <EmptyState
          icon="sparkles"
          title="Este video no se ha analizado"
          body="La transcripción se genera durante el análisis. Todavía no se ha ejecutado para este video."
          action={
            <Btn
              variant="solid"
              icon="sparkles"
              onClick={() => navigate('/library/' + assetId + '/analyse')}
            >
              Analizar video
            </Btn>
          }
        />
      </Panel>
    );
  }

  // In flight — not an answer yet, so neither "no speech" nor an error.
  if (busy || status === 'pending' || status === 'processing') {
    return (
      <Panel title="Transcripción">
        <EmptyState
          icon="clock"
          title="Transcripción en proceso"
          body="El análisis está corriendo. Las líneas aparecerán cuando termine."
        />
      </Panel>
    );
  }

  if (status === 'failed') {
    return (
      <Panel title="Transcripción">
        <EmptyState
          icon="alertTriangle"
          title="El análisis falló"
          body="No hay transcripción porque el análisis de este video terminó en error. Vuelve a ejecutarlo."
          action={
            <Btn
              variant="solid"
              icon="refresh"
              onClick={() => navigate('/library/' + assetId + '/analyse')}
            >
              Reintentar análisis
            </Btn>
          }
        />
      </Panel>
    );
  }

  // STATE 3 — analysis completed and produced nothing.
  //
  // This is a real answer ONLY if the enrichment (stage-2) half actually ran.
  // Stage-1 scene detection completes without ever attempting transcription,
  // and nothing auto-dispatches enrichment after it — so for the ordinary state
  // of this fleet (analysed, never enriched) claiming "no speech" would be a
  // confident false statement about the video's content.
  if (status === 'completed' && enrichmentCompleted) {
    return (
      <Panel title="Transcripción">
        <EmptyState
          icon="fileText"
          title="No se detectó voz"
          body="La transcripción terminó y no encontró diálogo transcribible en este video."
        />
      </Panel>
    );
  }

  // STATE 3b — stage-1 done, stage-2 never ran. Absent metadata, NOT absent
  // speech. Offer the action that would produce it.
  if (status === 'completed') {
    return (
      <Panel title="Transcripción">
        <EmptyState
          icon="fileText"
          title="Sin transcripción todavía"
          body="El análisis de escenas terminó, pero la transcripción aún no se ha ejecutado para este video. No se sabe si contiene diálogo."
          action={
            <Btn
              variant="solid"
              icon="sparkles"
              onClick={() => navigate('/library/' + assetId + '/analyse')}
            >
              Ejecutar transcripción
            </Btn>
          }
        />
      </Panel>
    );
  }

  // Fail closed. An unrecognised status is NOT "no speech detected".
  return (
    <Panel title="Transcripción">
      <EmptyState
        icon="alertTriangle"
        title="Estado de análisis desconocido"
        body={`No hay transcripción y el estado reportado ("${status}") no es uno que esta pantalla sepa interpretar. No se asume que el video carezca de voz.`}
      />
    </Panel>
  );
}

/* ------------------------------------------------------------------ *
 * The v2 cue-level transcript (card MA-82)
 * ------------------------------------------------------------------ */

const STAGE_WORDS: Record<string, string> = {
  extract: 'extracción',
  understand: 'comprensión',
  caption: 'subtítulos e índice',
  persist: 'persistencia',
};

function TranscriptCues({
  assetId,
  run,
  onSeekMs,
}: {
  assetId: string;
  run: RunStatusView;
  onSeekMs?: (ms: number) => void;
}): React.ReactElement {
  const [shown, setShown] = useState(PAGE_SIZE);
  const { results, status, loadMore } = usePaginatedQuery(
    mediaAnalysisApi.queries.listTranscriptCues,
    { runId: run.runId } as never,
    { initialNumItems: CUES_PAGE },
  ) as { results: TranscriptCue[]; status: string; loadMore: (n: number) => void };

  // Every page is fetched: the downloads must equal the run's cues, not a prefix of them.
  useEffect(() => {
    if (status === 'CanLoadMore') loadMore(CUES_PAGE);
  }, [status, loadMore]);

  const running = run.status === 'running';
  const complete = status === 'Exhausted';
  const cues = results;

  if (status === 'LoadingFirstPage') {
    return (
      <Panel title="Transcripción">
        <SkeletonList rows={8} />
      </Panel>
    );
  }

  const inProgress = running ? (
    <span data-testid="transcript-in-progress">
      <Pill tone="warn" size="sm" dot>en proceso · {STAGE_WORDS[run.stage] ?? run.stage}</Pill>
    </span>
  ) : null;

  if (cues.length === 0 && complete) {
    if (running) {
      return (
        <Panel title="Transcripción" right={inProgress}>
          <EmptyState
            icon="clock"
            title="Transcripción en proceso"
            body={`El análisis está en la etapa de ${STAGE_WORDS[run.stage] ?? run.stage}. Las líneas aparecerán cuando la transcripción se haya guardado.`}
          />
        </Panel>
      );
    }
    const word = run.modalities?.transcript;
    if (word === 'skipped') {
      return (
        <Panel title="Transcripción">
          <EmptyState
            icon="fileText"
            title="Transcripción omitida"
            body="El análisis terminó y no encontró voz que transcribir en este video (transcripción: omitido)."
          />
        </Panel>
      );
    }
    if (word === 'failed') {
      return (
        <Panel title="Transcripción">
          <EmptyState
            icon="alertTriangle"
            title="La transcripción falló"
            body={`${failureWord('transcript_failed')}: el análisis terminó sin transcripción.`}
            action={<Btn variant="solid" icon="refresh" onClick={() => navigate('/library/' + assetId + '/analyse')}>Reintentar análisis</Btn>}
          />
        </Panel>
      );
    }
    return (
      <Panel title="Transcripción">
        <EmptyState
          icon="fileText"
          title="No se detectó voz"
          body="La transcripción terminó y no encontró diálogo transcribible en este video."
        />
      </Panel>
    );
  }

  const visible = cues.slice(0, shown);
  const remaining = cues.length - visible.length;
  const downloadTitle = complete ? undefined : 'Cargando todas las líneas…';
  const base = `transcripcion-${assetId}`;

  return (
    <Panel
      title="Transcripción"
      right={
        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          {inProgress}
          <Pill tone="neutral" size="sm">
            {cues.length}{complete ? '' : '+'} {cues.length === 1 ? 'línea' : 'líneas'}
          </Pill>
          <span data-testid="transcript-download-vtt">
            <Btn size="sm" variant="ghost" icon="download" disabled={!complete} title={downloadTitle}
              onClick={() => downloadTextFile(`${base}.vtt`, 'text/vtt', cuesToWebVtt(cues))}>
              WebVTT
            </Btn>
          </span>
          <span data-testid="transcript-download-srt">
            <Btn size="sm" variant="ghost" icon="download" disabled={!complete} title={downloadTitle}
              onClick={() => downloadTextFile(`${base}.srt`, 'application/x-subrip', cuesToSubRip(cues))}>
              SubRip
            </Btn>
          </span>
        </span>
      }
    >
      <div
        data-testid="transcript-automatic"
        style={{
          fontSize: 12,
          color: 'var(--body-muted)',
          background: 'var(--lightest-gray)',
          borderRadius: 8,
          padding: '8px 10px',
          marginBottom: 14,
        }}
      >
        Transcripción automática. Las líneas con confianza baja (menor a {LOW_CONFIDENCE}) aparecen atenuadas.
        {running ? ' El análisis sigue en curso: la transcripción puede completarse.' : ''}
      </div>
      <ol data-testid="transcript-cues" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {visible.map((cue) => (
          <CueLine key={cue._id} cue={cue} onSeekMs={onSeekMs} />
        ))}
      </ol>
      {remaining > 0 && (
        <div style={{ marginTop: 14, textAlign: 'center' }}>
          <Btn variant="ghost" onClick={() => setShown((n) => n + PAGE_SIZE)}>
            Mostrar más ({remaining} restantes)
          </Btn>
        </div>
      )}
    </Panel>
  );
}

/** One cue: its start, its text, shaded below `LOW_CONFIDENCE`; a button when a seek handle exists. */
function CueLine({ cue, onSeekMs }: { cue: TranscriptCue; onSeekMs?: (ms: number) => void }): React.ReactElement {
  const clock = fmtClock(cue.startMs);
  const low = cue.confidence < LOW_CONFIDENCE;
  const seekable = typeof onSeekMs === 'function';
  const body = (
    <>
      <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', flexShrink: 0, width: 72, textAlign: 'left', paddingTop: 2 }}>
        {clock}
      </span>
      <span style={{ fontSize: 14, lineHeight: 1.55, textAlign: 'left' }}>
        {cue.speaker ? <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginRight: 6 }}>{cue.speaker}</span> : null}
        {cue.text}
      </span>
    </>
  );
  const rowStyle: React.CSSProperties = {
    display: 'flex',
    gap: 12,
    width: '100%',
    padding: '7px 8px',
    border: 'none',
    borderRadius: 8,
    background: 'none',
    font: 'inherit',
    color: 'var(--ink)',
    opacity: low ? 0.55 : 1,
  };
  return (
    <li
      data-testid="transcript-cue"
      data-cue-index={cue.cueIndex}
      data-low-confidence={low ? 'true' : 'false'}
      title={low ? `Confianza ${cue.confidence.toFixed(2)}` : undefined}
      style={{ borderBottom: '1px solid var(--lightest-gray)' }}
    >
      {seekable ? (
        <button type="button" onClick={() => onSeekMs?.(cue.startMs)} title={`Ir a ${clock}`} style={{ ...rowStyle, cursor: 'pointer' }}>
          {body}
        </button>
      ) : (
        <div style={rowStyle}>{body}</div>
      )}
    </li>
  );
}

/**
 * One time-coded line. `transcript` is the spoken text — and the ONLY thing
 * this tab renders.
 *
 * It deliberately does NOT fall back to `caption`. A caption is Gemini's
 * description of what the frame LOOKS like; putting it on a line in a tab
 * titled "Transcripción" would present a guess about pictures as a record of
 * speech. Since the 2026-08-20 rescope stage-2 emits `transcript: null` for
 * every segment, that fallback would have been the only path taken — i.e. the
 * tab would have shown nothing but mislabelled captions. Caption text is
 * reachable through content search, which labels it "Descripción visual".
 */
function TranscriptLine({
  segment,
  onSeekMs,
}: {
  segment: TranscriptSegment;
  onSeekMs?: (ms: number) => void;
}): React.ReactElement {
  const text = (segment.transcript ?? '').trim();
  const clock = fmtClock(segment.startTimeMs);
  const seekable = typeof onSeekMs === 'function';

  const body = (
    <>
      <span
        className="t-mono"
        style={{
          fontSize: 11,
          color: 'var(--muted-slate)',
          flexShrink: 0,
          width: 72,
          textAlign: 'left',
          paddingTop: 2,
        }}
      >
        {clock}
      </span>
      <span style={{ fontSize: 14, lineHeight: 1.55, textAlign: 'left' }}>
        {text || (
          <em style={{ color: 'var(--muted-slate)' }}>(sin texto en este segmento)</em>
        )}
      </span>
    </>
  );

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    gap: 12,
    width: '100%',
    padding: '7px 8px',
    border: 'none',
    borderRadius: 8,
    background: 'none',
    font: 'inherit',
    color: 'var(--ink)',
  };

  return (
    <li style={{ borderBottom: '1px solid var(--lightest-gray)' }}>
      {seekable ? (
        <button
          type="button"
          onClick={() => onSeekMs?.(segment.startTimeMs)}
          title={`Ir a ${clock}`}
          style={{ ...rowStyle, cursor: 'pointer' }}
        >
          {body}
        </button>
      ) : (
        <div style={rowStyle}>{body}</div>
      )}
    </li>
  );
}

export default TranscriptTab;
